import { useState, useEffect, ChangeEvent } from 'react';
import StepProgress from '../../../juristec-ui/core/StepProgress';
import { Filters, SelectFileAndBindColumns, Submit } from '../CopyDashTemplate';
import {
  File, FilterColumn, NewDashName, TourContext,
} from '../CopyDashTemplate/types';
import { verifyInput } from '../../../juristec-ui/utils/validators/inputTextValidators';
import { SelectOption } from '../../../types';

interface Variables {
  [key: string]: {
    description: string;
    type: string;
  };
}

interface SubmitData {
  name: string;
  bindings: Record<string, string>;
  filters: Array<{
    column: string;
    selector: string;
    values: (string | number)[];
  }>;
  database: string;
}

interface FileVariable {
  id: string
  label: string
  value: string
  type: string
}

interface CopyDashboardProps {
  hide: () => void;
  variables: Variables;
  files: File[];
  handleFileChange: (fileId: string) => Promise<FileVariable[]>;
  onSelectFilter: (fileId: string, column: string, dateFormat?: string) => Promise<SelectOption[]>;
  submitData: (data: SubmitData) => void;
  tourContext?: TourContext;
}

const copyDashboardDefaultPropTourContext: TourContext = {
  tourOpen: false,
};

/**
 * Component with steps to apply a template to a file
 */
const CopyDashboard = ({
  hide,
  variables,
  files,
  handleFileChange,
  onSelectFilter,
  submitData,
  tourContext = copyDashboardDefaultPropTourContext,
}: CopyDashboardProps) => {
  const [columnVarBinds, setColumnVarBinds] = useState<Record<string, string>>({});
  const [filterColumns, setFilterColumns] = useState<FilterColumn[]>([]);
  const [newDashName, setNewDashName] = useState<NewDashName>({
    value: '',
    error: true,
    errorMsg: '',
  });
  const [selectedFile, setSelectedFile] = useState<
  { value: string; label: string; owner: string }
  >({
    value: '',
    label: '',
    owner: '',
  });
  const [selectedFileVars, setSelectedFileVars] = useState<FileVariable[]>([]);

  useEffect(() => {
    if (!selectedFile.value) return;
    (async () => {
      setColumnVarBinds({});
      setFilterColumns([]);
      const fileVars = await handleFileChange(selectedFile.value);
      setSelectedFileVars(fileVars);
      setNewDashName({
        value: '',
        error: true,
        errorMsg: '',
      });
    })();
  }, [selectedFile]);

  const getColumnData = (column: string, selector: string) => onSelectFilter(
    selectedFile.value, column, selector,
  );

  const checkVarBinds = () => Object.keys(variables).length === Object.keys(columnVarBinds).length;

  const newDashNameHandle = (val: string | ChangeEvent<HTMLInputElement>): void => {
    const msg = verifyInput(val, true, 2, 40);
    setNewDashName({
      value: val as string,
      error: msg.length !== 0,
      errorMsg: msg,
    });
  };

  const checkNewDashName = () => {
    if (newDashName.error) {
      newDashNameHandle(newDashName.value);
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    const data: SubmitData = {
      name: newDashName.value,
      bindings: columnVarBinds,
      filters: filterColumns,
      database: selectedFile.value,
    };
    submitData(data);
  };

  return (
    <StepProgress
      steps={[
        {
          label: 'Arquivo e Variáveis',
          validation: checkVarBinds,
          content: (
            <SelectFileAndBindColumns
              variables={variables}
              columnVarBinds={columnVarBinds}
              setColumnVarBinds={setColumnVarBinds}
              files={files}
              selectedFileVars={selectedFileVars}
              selectedFile={selectedFile}
              setSelectedFile={setSelectedFile}
            />
          ),
        },
        {
          label: 'Filtro (Opcional)',
          content: (
            <Filters
              fileColumns={selectedFileVars}
              getColumnData={getColumnData}
              filterColumns={filterColumns}
              setFilterColumns={setFilterColumns}
            />
          ),
        },
        {
          label: 'Criação',
          validation: checkNewDashName,
          content: <Submit newDashName={newDashName} newDashNameHandle={newDashNameHandle} />,
        },
      ]}
      hide={hide}
      complete={handleSubmit}
      markersWidth="large"
      tourContext={tourContext}
    />
  );
};

export default CopyDashboard;
